import stockissueService from '../../../services/stockissueService';
import filterService from '../../../services/filterService';

import {
    setStockIssueLoading,
    setStockIssueError,
    setStockIssueList,
    setStockIssueCurrent,
    setStockIssuePagination,
    setStockIssueSort,
    setStockIssueFilters,
    setStockIssueDefaultFilter,
    setStockIssueSettings,
    clearStockIssueCurrent,
    clearStockIssueError,
    setStockIssueFilterLoading,
    changeStockIssueFilterCondition,
    addStockIssueFilterCondition,
    updateStockIssueFilterConditionRule,
    updateStockIssueFilterConditionValue,
    removeStockIssueFilterCondition,
    setStockIssueSelected,
    resetStockIssueFiltersAndPagination
} from './stockissueSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().stockissue.isSettingsInitialized) {
            dispatch(setStockIssueLoading(true));
            const response = await stockissueService.getSettings();

            // Set the settings
            dispatch(setStockIssueSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setStockIssueDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setStockIssueError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setStockIssueLoading(false));
    }
};

export const fetchStockissues = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().stockissue;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setStockIssueLoading(true));
        const response = await stockissueService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setStockIssueList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchStockissues:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setStockIssueError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setStockIssueLoading(false));
    }
};

export const fetchStockIssue = (id) => async (dispatch) => {
    try {
        dispatch(setStockIssueLoading(true));
        const response = await stockissueService.getOne(id);
        dispatch(setStockIssueCurrent(response.data));
    } catch (error) {
        dispatch(setStockIssueError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setStockIssueLoading(false));
    }
};

export const createStockIssue = (data) => async (dispatch) => {
    try {
        const response = await stockissueService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateStockIssue = (id, data) => async (dispatch) => {
    try {
        const response = await stockissueService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteStockIssue = (id) => async (dispatch) => {
    try {
        dispatch(setStockIssueLoading(true));
        const response = await stockissueService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setStockIssueError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setStockIssueLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setStockIssuePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setStockIssueSort(sort));
};

export const searchStockissues = (query) => (dispatch, getState) => {
    const currentFilters = getState().stockissue.filters;
    dispatch(setStockIssueFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setStockIssueDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeStockIssueFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addStockIssueFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateStockIssueFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateStockIssueFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeStockIssueFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setStockIssueFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().stockissue.settings;
            dispatch(setStockIssueSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setStockIssueFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setStockIssueFilterLoading(false));
        dispatch(setStockIssueError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setStockIssueSelected(selectedRows));
    };
};

export const clearCurrentStockIssue = () => {
    return (dispatch) => {
        dispatch(clearStockIssueCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetStockIssueFiltersAndPagination());
};